import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { useInterventionsList } from './hooks/useInterventionsList';
import type { InterventionCTA } from './hooks/useInterventionsList';
import { getApiBaseUrl } from '../../../core/config/api';

/** Valeurs `cta_intervention.status` côté API (voir mapStatutToDb sur le serveur). */
const SCENARIO_STATUS_OPTIONS = [
  { value: 'en_creation', label: 'En création' },
  { value: 'en_test', label: 'En test' },
  { value: 'actif', label: 'Actif' },
] as const;

type ScenarioStatusValue = (typeof SCENARIO_STATUS_OPTIONS)[number]['value'];

interface InterventionHistoryEntry {
  id: number;
  action: 'created' | 'updated' | 'status_changed' | 'validated' | string;
  actor_username?: string | null;
  previous_status?: string | null;
  new_status?: string | null;
  details?: { changed_fields?: string[] } | null;
  created_at: string;
}

function normalizeScenarioStatus(raw: string): ScenarioStatusValue {
  const u = (raw || '').trim().toLowerCase();
  if (u === 'actif' || u === 'active') return 'actif';
  if (u === 'en_test') return 'en_test';
  return 'en_creation';
}

function scenarioStatusSelectRingClass(statusNorm: ScenarioStatusValue): string {
  if (statusNorm === 'actif') return 'border-emerald-600/60 ring-1 ring-emerald-500/25';
  if (statusNorm === 'en_test') return 'border-amber-600/60 ring-1 ring-amber-500/25';
  return 'border-gray-600 ring-1 ring-gray-600/30';
}

/** Bâtiment éligible pour /api/cta/generate-intervention (secteur 5 km + lié CTA). */
async function pickSpawnBuildingId(
  token: string,
  apiUrl: string
): Promise<{ id: number; error?: string }> {
  const res = await fetch(`${apiUrl}/api/buildings`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  if (!data.success || !Array.isArray(data.data)) {
    return { id: 0, error: 'Impossible de charger vos bâtiments.' };
  }
  const eligible = data.data.filter(
    (b: { type?: string; actif?: boolean; cta_id?: unknown; localisation?: string }) =>
      b &&
      b.type !== 'cta' &&
      b.actif &&
      b.cta_id != null &&
      b.localisation &&
      String(b.localisation).trim() !== ''
  );
  if (eligible.length === 0) {
    return {
      id: 0,
      error:
        'Aucun site opérationnel actif, géolocalisé et lié à un CTA. En jeu : créez un site, liez-le au CTA, puis réessayez.',
    };
  }
  return { id: Number(eligible[0].id) };
}

const InterventionsManage: React.FC = () => {
  const navigate = useNavigate();
  const { interventions, loading, error, reload } = useInterventionsList();
  const [spawnMsg, setSpawnMsg] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState<number | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<number | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [historyOpenId, setHistoryOpenId] = useState<number | null>(null);
  const [historyById, setHistoryById] = useState<Record<number, InterventionHistoryEntry[]>>({});
  const [historyLoadingId, setHistoryLoadingId] = useState<number | null>(null);

  const changeScenarioStatut = async (intervention: InterventionCTA, newStatut: ScenarioStatusValue) => {
    const current = normalizeScenarioStatus(intervention.statut);
    if (newStatut === current) return;
    setStatusMsg(null);
    setStatusBusyId(intervention.id);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const res = await fetch(`${apiUrl}/api/cta/interventions/${intervention.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ statut: newStatut }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && !data.error) {
        setStatusMsg(`Erreur HTTP ${res.status}`);
        return;
      }
      if (data.success) {
        await reload();
        setStatusMsg(null);
      } else {
        setStatusMsg(data.error || 'Impossible de mettre à jour le statut.');
      }
    } catch (e: unknown) {
      setStatusMsg(e instanceof Error ? e.message : 'Erreur réseau.');
    } finally {
      setStatusBusyId(null);
    }
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('fr-FR');
  };

  const actionLabel = (action: string): string => {
    if (action === 'created') return 'Création';
    if (action === 'updated') return 'Modification';
    if (action === 'status_changed') return 'Changement de statut';
    if (action === 'validated') return 'Validation';
    return action;
  };

  const openHistoryModal = async (interventionId: number) => {
    setHistoryOpenId(interventionId);
    if (historyById[interventionId]) return;

    setHistoryLoadingId(interventionId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const res = await fetch(`${apiUrl}/api/cta/interventions/${interventionId}/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setStatusMsg(data.error || `Impossible de charger l'historique (HTTP ${res.status})`);
        return;
      }
      setHistoryById((prev) => ({
        ...prev,
        [interventionId]: Array.isArray(data.data) ? data.data : [],
      }));
    } catch (e: unknown) {
      setStatusMsg(e instanceof Error ? e.message : "Erreur lors du chargement de l'historique.");
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const spawnTestOnMyAccount = async (scenarioId: number) => {
    setSpawnMsg(null);
    setSpawnBusy(scenarioId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSpawnMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const pick = await pickSpawnBuildingId(token, apiUrl);
      if (!pick.id) {
        setSpawnMsg(pick.error || 'Pas de bâtiment éligible pour la génération.');
        return;
      }
      const res = await fetch(`${apiUrl}/api/cta/generate-intervention`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_id: scenarioId,
          departement_code: '75',
          player_building_id: pick.id,
          description_joueur: 'Test PIO1 — lancé depuis le panel (position aléatoire dans le secteur 5 km)',
        }),
      });
      const data = await res.json();
      if (data.success) {
        const num = data.data?.numero_intervention ?? data.data?.id;
        setSpawnMsg(
          `Instance créée (${num}). Ouvrez la carte en jeu : menu interventions à droite.`
        );
      } else {
        setSpawnMsg(data.error || 'Échec de la génération.');
      }
    } catch (e: unknown) {
      setSpawnMsg(e instanceof Error ? e.message : 'Erreur réseau');
    } finally {
      setSpawnBusy(null);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                Gérer les scénarios d'interventions
              </h1>
              <p className="text-gray-300">
                Liste et gestion des scénarios d'interventions CTA
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/panel/interventions/create')}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                Créer un scénario
              </button>
              <button
                onClick={() => reload()}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                title="Actualiser la liste"
              >
                Actualiser
              </button>
              <button
                onClick={() => navigate('/panel')}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
              >
                Retour
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}

          {spawnMsg && (
            <div
              className={`mb-4 p-4 rounded-lg border ${
                spawnMsg.startsWith('Instance créée')
                  ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-100'
                  : 'bg-amber-500/15 border-amber-500/50 text-amber-100'
              }`}
            >
              {spawnMsg}
            </div>
          )}

          {statusMsg && (
            <div className="mb-4 p-4 rounded-lg border bg-red-500/15 border-red-500/50 text-red-100">
              {statusMsg}
            </div>
          )}

          <Card className="p-6">
            {loading ? (
              <div className="text-center py-12 text-white">
                Chargement des scénarios...
              </div>
            ) : interventions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                Aucun scénario d'intervention trouvé.
                <br />
                <button
                  onClick={() => navigate('/panel/interventions/create')}
                  className="mt-4 px-6 py-2 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg hover:from-primary-700 hover:to-purple-700 transition-all"
                >
                  Créer le premier scénario
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-3 text-gray-300 font-semibold">ID</th>
                      <th className="pb-3 text-gray-300 font-semibold">Service</th>
                      <th className="pb-3 text-gray-300 font-semibold">Catégorie</th>
                      <th className="pb-3 text-gray-300 font-semibold">Statut</th>
                      <th className="pb-3 text-gray-300 font-semibold">Exceptionnelle</th>
                      <th className="pb-3 text-gray-300 font-semibold">Créé</th>
                      <th className="pb-3 text-gray-300 font-semibold">Dernière modif</th>
                      <th className="pb-3 text-gray-300 font-semibold">Validation</th>
                      <th className="pb-3 text-gray-300 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interventions.map((intervention) => (
                      <tr key={intervention.id} className="border-b border-gray-800">
                        <td className="py-3 text-white">{intervention.id}</td>
                        <td className="py-3 text-white">{intervention.service.toUpperCase()}</td>
                        <td className="py-3 text-white">{intervention.categorie}</td>
                        <td className="py-3">
                          {(() => {
                            const norm = normalizeScenarioStatus(intervention.statut);
                            return (
                              <select
                                value={norm}
                                disabled={statusBusyId === intervention.id}
                                onChange={(e) =>
                                  changeScenarioStatut(
                                    intervention,
                                    e.target.value as ScenarioStatusValue
                                  )
                                }
                                className={`min-w-[10.5rem] rounded-md bg-gray-900/80 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${scenarioStatusSelectRingClass(norm)}`}
                                title="Changer le statut du scénario"
                                aria-label={`Statut du scénario ${intervention.id}`}
                              >
                                {SCENARIO_STATUS_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </td>
                        <td className="py-3 text-gray-300">
                          {intervention.is_exceptionnelle === 1 ||
                          intervention.is_exceptionnelle === true ? (
                            <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-300">
                              Oui
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-3 text-gray-400">
                          <div>{formatDateTime(intervention.created_at)}</div>
                          <div className="text-xs text-gray-500">
                            {intervention.created_by_username || 'Inconnu'}
                          </div>
                        </td>
                        <td className="py-3 text-gray-400">
                          <div>{formatDateTime(intervention.last_modified_at || intervention.updated_at)}</div>
                          <div className="text-xs text-gray-500">
                            {intervention.last_modified_by_username || 'Inconnu'}
                          </div>
                        </td>
                        <td className="py-3 text-gray-400">
                          <div>{formatDateTime(intervention.validated_at)}</div>
                          <div className="text-xs text-gray-500">
                            {intervention.validated_by_username || '—'}
                          </div>
                        </td>
                        <td className="py-3 flex flex-wrap gap-2">
                          <button
                            onClick={() => navigate(`/panel/interventions/edit/${intervention.id}`)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            disabled={spawnBusy === intervention.id}
                            onClick={() => spawnTestOnMyAccount(intervention.id)}
                            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm transition-colors"
                            title="Crée une instance sur votre compte (test carte)"
                          >
                            {spawnBusy === intervention.id ? '…' : 'Tester sur mon compte'}
                          </button>
                          <button
                            type="button"
                            disabled={historyLoadingId === intervention.id}
                            onClick={() => openHistoryModal(intervention.id)}
                            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded text-sm transition-colors"
                          >
                            {historyLoadingId === intervention.id ? 'Chargement...' : 'Historique'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
      {historyOpenId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setHistoryOpenId(null)}
        >
          <div
            className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-700 px-5 py-4">
              <h2 className="text-lg font-semibold text-white">
                Historique complet de l'intervention #{historyOpenId}
              </h2>
              <button
                type="button"
                onClick={() => setHistoryOpenId(null)}
                className="rounded-md bg-gray-700 px-3 py-1.5 text-sm text-white hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5">
              {(historyById[historyOpenId] || []).length === 0 ? (
                <div className="text-sm text-gray-400">
                  Aucun événement enregistré pour cette intervention.
                </div>
              ) : (
                <div className="space-y-2">
                  {(historyById[historyOpenId] || []).map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded border border-gray-700 bg-black/20 px-3 py-2 text-sm text-gray-200"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">
                          {actionLabel(entry.action)}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span>{formatDateTime(entry.created_at)}</span>
                        <span className="text-gray-400">•</span>
                        <span>
                          par <span className="text-white">{entry.actor_username || 'Inconnu'}</span>
                        </span>
                      </div>
                      {(entry.previous_status || entry.new_status) && (
                        <div className="text-xs text-gray-400 mt-1">
                          Statut: {entry.previous_status || '—'} → {entry.new_status || '—'}
                        </div>
                      )}
                      {(entry.details?.changed_fields?.length ?? 0) > 0 && (
                        <div className="text-xs text-gray-400 mt-1">
                          Champs modifiés: {entry.details?.changed_fields?.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionsManage;

